import React, { useEffect, useReducer, useState } from 'react';
import { getResourcesForClass, calculateResourceMax, rechargesOnRest, resolveLevelTable, getCustomResourcePool } from '../data/classResources';
import { getResourcesForRace } from '../data/raceResources';
import { getClassData } from '../utils/contentResolver';
import { systemBus, EVENTS } from '../services/systemBus';
import { effectCalculator } from '../services/effectCalculator';
import { useCharacter } from '../contexts/CharacterContext';
import { useInventory } from '../contexts/InventoryContext';
// useUI removed — rest buttons moved to RestPanel (F-28)
import useIsMobile from '../hooks/useIsMobile';
// [Sub-plan 1H-G-bw Stone 4 — 2026-05-30]: LoH "Spend HP" affordance.
import UsableAbilityModal from './UsableAbilityModal';
// [A49 AT-2 — 2026-06-03]: Slot-recovery invoke-anytime surface.
//   Reuses the same modal RestPanel uses — no fork.
import SlotRecoveryModal from './SlotRecoveryModal';
// [Sub-plan A2-classres-consumers — 2026-05-30]: Breath Weapon ancestry helpers.
import { getBreathWeaponDamageType, getBreathWeaponShape } from '../utils/contentResolver';
// [Sub-plan A2-classres-consumers — 2026-05-30 Rule 22]: Shared pure-formula helpers.
import { scaleBreathWeaponDice, computeAbilitySaveDc, inferBreathWeaponSave, applyHalfOnSave } from '../utils/usableAbilityFormulas';
// [NS-QB-rage-scaling — 2026-05-31 Rule 22]: Shared level-scaled rage shadow-entry builder.
import { buildRageShadowEntry } from '../utils/rageShadowEntry';
// [A49 AT-2 Rule 22 — 2026-06-03]: Pure slot-mutation helpers (extract→import→assert contract).
import { applySlotDeltas, decrementActionPool } from '../utils/slotRecoveryApply';

// [PURPOSE]: Display and manage class-specific resources (superiority dice, ki points, rage, etc.)
// [WHY]: Battle Masters need superiority dice tracking, Monks need ki, Barbarians need rage, etc.
// [INTEGRATION]: Used in Character sheet to track per-class resources separate from spell slots

const ClassResourcesPanel = ({
  isDemo = false  // [PHASE 4B - KEPT]: isDemo is component-specific prop
}) => {
  // [PHASE 4B - MIGRATED]: Now using context hooks instead of props
  // [A49 AT-2 — 2026-06-03]: spellSlots + setSpellSlots needed for SlotRecoveryModal onConfirm.
  const { playerStats: character, setPlayerStats: setCharacter, logActivity = () => {}, activeQuickBonuses = [], setActiveQuickBonuses = null, spellSlots = {}, setSpellSlots = () => {} } = useCharacter();
  const { inventory = [] } = useInventory();
  const isMobile = useIsMobile();

  // [Sub-plan 1H-G-bw Stone 4 — 2026-05-30]: LoH "Spend HP" modal state.
  // `lohModalResource` carries the resource object whose tile triggered the modal
  // so onResolve can reference its current max and classIndex.
  // handleLohResolve is defined further down (after updateResource) to keep the
  // closure correct; this section only declares the React state hooks.
  const [lohModalOpen, setLohModalOpen] = useState(false);
  const [lohModalResource, setLohModalResource] = useState(null);

  // [Sub-plan A2-classres-consumers — 2026-05-30]: Breath Weapon modal state.
  const [breathWeaponModalOpen, setBreathWeaponModalOpen] = useState(false);
  const [breathWeaponModalSpec, setBreathWeaponModalSpec] = useState(null);

  // [Sub-plan A2-classres-consumers — 2026-05-30]: Hellish Rebuke modal state.
  const [hellishRebukeModalOpen, setHellishRebukeModalOpen] = useState(false);
  const [hellishRebukeModalSpec, setHellishRebukeModalSpec] = useState(null);

  // [Sub-plan A2-classres-consumers — 2026-05-30]: Spend-option rollSpec modal state.
  // Generic: covers any spend option with option.rollSpec (currently Stunning Strike).
  const [spendOptionModalOpen, setSpendOptionModalOpen] = useState(false);
  const [spendOptionModalState, setSpendOptionModalState] = useState(null); // { resource, option, rollSpec }

  // [A49 AT-2 — 2026-06-03]: Slot-recovery invoke-anytime modal state.
  //   null = closed; otherwise = the slotRecoveryAction entry from
  //   effectCalculator.getSlotRecoveryActions whose invokeAnytime===true
  //   button was clicked.
  const [activeSlotRecoveryAction, setActiveSlotRecoveryAction] = useState(null);

  // [PURPOSE]: Force re-render mechanism for event subscriptions
  // [WHY]: Resource maximums depend on class level and CHA modifier (Bardic Inspiration)
  // [INTEGRATION]: When level/class/stats change, resources must recalculate
  const [, forceUpdate] = useReducer(x => x + 1, 0);

  // [PURPOSE]: Subscribe to all events that affect class resources
  // [WHY]: Ki = monk level, rage = barbarian level, bardic inspiration = CHA modifier
  // [INTEGRATION]: systemBus events trigger re-render for reactive resource updates
  useEffect(() => {
    const handleResourceChange = (eventName, data) => {
      forceUpdate(); // Force re-render to recalculate resource maximums
    };

    // Level changes affect resource maximums (ki, sorcery, lay on hands, etc.)
    const unsubscribeLevelUp = systemBus.on(EVENTS.LEVEL_UP, (data) =>
      handleResourceChange('LEVEL_UP', data));

    // Class changes affect which resources are available
    const unsubscribeClassChanged = systemBus.on(EVENTS.CLASS_CHANGED, (data) =>
      handleResourceChange('CLASS_CHANGED', data));

    // Subclass changes affect resources (Battle Master superiority dice)
    const unsubscribeSubclassChanged = systemBus.on(EVENTS.SUBCLASS_CHANGED, (data) =>
      handleResourceChange('SUBCLASS_CHANGED', data));

    // Rest events restore resources (short rest for ki/action surge, long rest for rage/sorcery)
    const unsubscribeLongRest = systemBus.on(EVENTS.REST_LONG_COMPLETED, (data) =>
      handleResourceChange('REST_LONG_COMPLETED', data));
    const unsubscribeShortRest = systemBus.on(EVENTS.REST_SHORT_COMPLETED, (data) =>
      handleResourceChange('REST_SHORT_COMPLETED', data));

    // Ability score changes affect Bardic Inspiration max (CHA modifier)
    const unsubscribeAbilityChanged = systemBus.on(EVENTS.ABILITY_SCORE_CHANGED, (data) =>
      handleResourceChange('ABILITY_SCORE_CHANGED', data));
    const unsubscribeStatsChanged = systemBus.on(EVENTS.STATS_CHANGED, (data) =>
      handleResourceChange('STATS_CHANGED', data));

    // [Phase 4b combat-panel-UI followup — APR 2026]: Race changes affect
    // racial resource availability (Half-Orc Relentless Endurance, etc.).
    const unsubscribeRaceChanged = systemBus.on(EVENTS.RACE_CHANGED, (data) =>
      handleResourceChange('RACE_CHANGED', data));

    // [Sub-plan A2-rule27-cap — 2026-05-30]: Spend-menu / usable-ability effects
    // (e.g. the Monk Ki Spend Menu spendActionMenu) are registered by
    // useClassFeatureEffects in a post-mount useEffect — AFTER this panel's first
    // render. getSpendActionMenus() reads effectRegistry at RENDER time, so without
    // this subscription the "Spend Options" pane never appears on a cold character
    // load (it only surfaced once some OTHER event — stats/level/rest — happened to
    // force a re-render). effectRegistry.register/remove/clear all emit EFFECTS_CHANGED;
    // subscribing here makes the panel reactive to effect (de)registration so spend
    // menus surface immediately. Diagnosed via the A2 visual cap (Rule 27): the unit
    // tests were data/source-scan only and never exercised this render-timing path.
    const unsubscribeEffectsChanged = systemBus.on(EVENTS.EFFECTS_CHANGED, (data) =>
      handleResourceChange('EFFECTS_CHANGED', data));

    // Cleanup: Unsubscribe from all events on unmount
    return () => {
      unsubscribeLevelUp();
      unsubscribeClassChanged();
      unsubscribeSubclassChanged();
      unsubscribeLongRest();
      unsubscribeShortRest();
      unsubscribeAbilityChanged();
      unsubscribeStatsChanged();
      unsubscribeRaceChanged();
      unsubscribeEffectsChanged();
    };
  }, []); // Empty deps - subscribe once on mount, cleanup on unmount

  // [TASK 5 — 2026-05-31 GAP-4]: Clear the shadow Rage QB entry on long rest.
  // The shadow entry (written by updateResource when rage is consumed) must be
  // cleared when the character long-rests so effectCalculator._buildContext stops
  // reading raging:true. Rage recharges on long rest (classResources.js Barbarian).
  // This useEffect uses `setActiveQuickBonuses` directly to avoid stale closure
  // by listing it as a dep (it is a stable setState setter from CharacterContext).
  useEffect(() => {
    if (!setActiveQuickBonuses) return;
    const clearRageShadow = () => {
      setActiveQuickBonuses(prev => prev.filter(b => !(b._shadow && b.type === 'rage') && !(b.id === 'rage' && b._shadow)));
    };
    const unsubLongRest = systemBus.on(EVENTS.REST_LONG_COMPLETED, clearRageShadow);
    return () => { unsubLongRest(); };
  }, [setActiveQuickBonuses]);

  // [PURPOSE]: Get all class resources for the character using data-driven definitions
  // [WHY]: Supports SRD classes + custom classes from Content Workshop via classResources.js
  // [INTEGRATION]: Uses same schema as RestPanel (getResourcesForClass + calculateResourceMax)
  const getClassResources = () => {
    const resources = [];

    if (!character.classes || !Array.isArray(character.classes)) {
      return resources;
    }

    character.classes.forEach((classEntry, classIndex) => {
      const className = classEntry.className;
      const classLevel = classEntry.level || 1;

      // [DATA-DRIVEN]: Get resource definitions from classResources.js (SRD) + custom class data
      const srdResources = getResourcesForClass(className, classEntry.subclass);
      const customClassData = getClassData(className);
      const customResources = customClassData?.resources || [];
      const allDefs = [...srdResources, ...customResources];

      allDefs.forEach(rawDef => {
        // [FIX APR 2026 — AUDIT M-17]: Default values for custom class resources
        const def = {
          id: rawDef.id || `custom_${rawDef.name || 'resource'}`,
          name: rawDef.name || 'Resource',
          description: rawDef.description || '',
          minLevel: rawDef.minLevel || 1,
          rechargeOn: rawDef.rechargeOn || 'long',
          maxCalculation: rawDef.maxCalculation || null,
          ...rawDef
        };
        // Skip resources the character hasn't unlocked yet
        if (classLevel < (def.minLevel || 1)) return;

        // Calculate max using data-driven formula
        let max = calculateResourceMax(def, classLevel, character);

        // [OVERRIDE]: For ability-mod-based resources, use effectCalculator for accuracy
        // [WHY]: calculateResourceMax uses raw ability scores; effectCalculator includes
        //        magic items (Headband of Charisma), feats, and temporary effects
        if (def.maxCalculation?.startsWith('chaMod')) {
          const chaMod = effectCalculator.getEffectiveModifier(
            character, inventory, activeQuickBonuses, 'charisma'
          );
          max = Math.max(1, chaMod);
        }

        // Read stored resource value (check both snake_case write key and camelCase legacy key)
        const camelKey = def.id.replace(/_([a-z])/g, (_, c) => c.toUpperCase());
        let stored;
        if (def.subclassKey) {
          stored = classEntry.subclassData?.[def.subclassKey];
        } else {
          stored = classEntry[def.id] || classEntry[camelKey];
        }
        const current = stored?.current ?? max;

        // Resolve die size from level table if defined
        const dieSize = resolveLevelTable(def.dieSize, classLevel) || stored?.dieSize || null;

        // Determine effective recharge type (handles conditional recharge like Bard Font of Inspiration)
        const effectiveRecharge = rechargesOnRest(def, 'short', classLevel) ? 'short rest' : 'long rest';

        // Build display name (Pact Magic includes slot level)
        let displayName = def.name;
        if (def.slotLevelTable) {
          const slotLevel = resolveLevelTable(def.slotLevelTable, classLevel) || 1;
          const ordinals = ['th', 'st', 'nd', 'rd'];
          displayName = `${def.name} (${slotLevel}${ordinals[slotLevel] || 'th'} level)`;
        }

        // Build description (dynamic for level-dependent text)
        let description = def.description;
        if (dieSize && def.description?.includes('die')) {
          description = description.replace('die', dieSize);
        }
        // [Q17 2026-05-16]: Data-driven "unlimited" handling.
        //   999 is the canonical sentinel for capstone-unlimited resources
        //   (Barbarian rage L20 PHB p.49, Druid Archdruid L20 PHB p.67).
        //   Resource-name-aware so any future capstone-unlimited resource
        //   (e.g. homebrew classes) renders naturally without hardcoding
        //   def.id checks (Rule 11 — no hardcoded class/feature strings).
        if (max >= 999) {
          description = `Unlimited ${def.name}!`;
        }

        resources.push({
          type: def.id,
          classIndex,
          name: displayName,
          icon: def.icon,
          current,
          max,
          dieSize,
          recharge: effectiveRecharge,
          color: def.color,
          description,
          subclassKey: def.subclassKey || null,
          // [Sub-plan 1H-G-bw Stone 4 — 2026-05-30]: pass through the
          // spendHpModal flag so the tile can render the "Spend HP" affordance.
          // Property-based (Rule 11) — no resource-name string check.
          spendHpModal: def.spendHpModal || false,
        });
      });
    });

    return resources;
  };

  /**
   * [Phase 4b combat-panel-UI followup — APR 2026]: Get racial resources from
   * raceResources.js. Stored at playerStats.raceResources keyed by resource id.
   * Returned with classIndex = -1 sentinel so updateResource knows to write to
   * the race path instead of classes[i].
   */
  const getRaceResources = () => {
    const raceName = character.basics?.race || character.race;
    const subraceName = character.basics?.subrace || character.subrace;
    if (!raceName) return [];
    const charLevel = character.level || 1;
    const defs = getResourcesForRace(raceName, subraceName);
    return defs
      .filter(def => charLevel >= (def.minLevel || 1))
      .map(def => {
        const max = calculateResourceMax(def, charLevel, character);
        const stored = character.raceResources?.[def.id];
        const current = stored?.current ?? max;
        const effectiveRecharge = def.rechargeOn === 'short_rest' ? 'short rest' : 'long rest';
        return {
          type: def.id,
          classIndex: -1, // sentinel: this is a race resource
          name: def.name,
          icon: def.icon,
          current,
          max,
          dieSize: null,
          recharge: effectiveRecharge,
          color: def.color,
          description: def.description,
          subclassKey: null,
          // [Sub-plan A2-classres-consumers — 2026-05-30]: Pass through the
          // useAbilityModal flag so the tile can render a "Use" / "Use Reaction"
          // button. Property-based (Rule 11) — no resource-name string check.
          useAbilityModal: def.useAbilityModal || false,
        };
      });
  };

  // [PURPOSE]: Update a specific resource's current value
  // [DATA-DRIVEN]: Handles both standard resources (classEntry[id]) and subclass resources (subclassData[key])
  const updateResource = (classIndex, resourceType, delta, resourceName = '') => {
    if (isDemo) return;

    // Find the resource definition to check if it's a subclass / race resource
    const resource = resources.find(r => r.classIndex === classIndex && r.type === resourceType);

    setCharacter(prev => {
      const updated = { ...prev };

      // [Phase 4b combat-panel-UI followup — APR 2026]: Race resource path
      // (classIndex = -1 sentinel from getRaceResources). Stored at
      // playerStats.raceResources[<id>] = { current, max }.
      if (classIndex === -1) {
        const stored = prev.raceResources?.[resourceType] || {};
        const max = stored.max || resource?.max || 0;
        const newCurrent = Math.max(0, Math.min((stored.current ?? max) + delta, max));
        updated.raceResources = {
          ...(prev.raceResources || {}),
          [resourceType]: { ...stored, current: newCurrent, max },
        };
        return updated;
      }

      // [Fix 1.10 — APR 2026]: Custom-feat pool resource path (classIndex = -2
      // sentinel from getCustomFeatPoolResources). Stored at
      // playerStats.customFeatPools[<poolId>] = { current, max }. resourceType
      // is the pool id (UUID-shape, not name-derived).
      if (classIndex === -2) {
        const stored = prev.customFeatPools?.[resourceType] || {};
        const max = stored.max || resource?.max || 0;
        const newCurrent = Math.max(0, Math.min((stored.current ?? max) + delta, max));
        updated.customFeatPools = {
          ...(prev.customFeatPools || {}),
          [resourceType]: { ...stored, current: newCurrent, max },
        };
        return updated;
      }

      updated.classes = [...prev.classes];
      updated.classes[classIndex] = { ...updated.classes[classIndex] };

      if (resource?.subclassKey) {
        // Subclass resource (e.g., superiorityDice stored in subclassData)
        if (!updated.classes[classIndex].subclassData) {
          updated.classes[classIndex].subclassData = {};
        } else {
          updated.classes[classIndex].subclassData = { ...updated.classes[classIndex].subclassData };
        }
        const stored = updated.classes[classIndex].subclassData[resource.subclassKey] || {};
        updated.classes[classIndex].subclassData[resource.subclassKey] = {
          ...stored,
          current: Math.max(0, Math.min((stored.current || 0) + delta, stored.max || resource.max || 0))
        };
      } else {
        // Standard resource (stored directly on classEntry by resource id)
        const stored = updated.classes[classIndex][resourceType] || {};
        updated.classes[classIndex][resourceType] = {
          ...stored,
          current: Math.max(0, Math.min((stored.current || 0) + delta, stored.max || resource?.max || 0))
        };
      }

      return updated;
    });

    // [ACTIVITY LOG]: Log resource usage
    if (resourceName && delta !== 0) {
      const action = delta < 0 ? 'Used' : 'Restored';
      const amount = Math.abs(delta);
      logActivity(
        `${action} ${amount} ${resourceName}`,
        'combat',
        { resourceType, delta, classIndex }
      );
    }

    // [FIX APR 2026 — F-25]: Emit event so other systems can react to resource changes
    systemBus.emit(EVENTS.STATS_CHANGED, { source: 'classResource', resourceType, delta });

    // [TASK 5 — 2026-05-31 GAP-4 shadow-entry path]: When a Rage use is consumed
    // (delta < 0, resourceType === 'rage'), write a shadow QB entry so that
    // effectCalculator.js _buildContext (line ~1223) can detect raging via
    // findQuickBonusOfKind(activeQuickBonuses, 'rage').
    // The rage kind in quickBonusKinds.js documents this as the canonical signal.
    // [NS-QB-rage-scaling — 2026-05-31]: Rage damage bonus now scales by Barbarian
    // level (PHB p.48: +2 at L1, +3 at L9, +4 at L16) via buildRageShadowEntry →
    // effectCalculator.calculateRageDamage (registry-driven rage_damage_bonus). The
    // prior flat value:2 breadcrumb is resolved; the builder is the single source of
    // the shadow shape (also imported by the 4G raging-context test, Rule 22).
    // The shadow entry is removed on long rest (see REST_LONG_COMPLETED subscriber below).
    if (resourceType === 'rage' && delta < 0 && setActiveQuickBonuses) {
      const rageEntry = buildRageShadowEntry(character);
      setActiveQuickBonuses(prev => {
        // Deduplicate — only one shadow entry at a time
        const withoutRage = prev.filter(b => b.type !== 'rage' && b.id !== 'rage');
        return [...withoutRage, rageEntry];
      });
    }
  };

  // [REMOVED APR 2026 — F-28]: Duplicate rest restoration removed.
  // RestPanel is the authoritative source for rest actions. ClassResourcesPanel only DISPLAYS and TRACKS.

  /**
   * [Fix 1.10 — APR 2026 release triage]: Surface custom-feat resource pools
   * alongside class + race resources. Pools live on customFeats[].resourcePool
   * (see FeatsPanel.saveCustomFeat); the player TAKES the feat by adding its
   * id to playerStats.feats. We render a tracker for any pool whose parent
   * feat is currently taken.
   *
   * Sentinel classIndex = -2 routes updateResource through the custom pool
   * write-path (playerStats.customFeatPools[<poolId>] = { current, max }).
   */
  const getCustomFeatPoolResources = () => {
    const takenFeats = character.feats || [];
    const customFeats = character.customFeats || [];
    const out = [];
    const formatRecharge = (raw) => raw === 'shortRest' ? 'short rest' : (raw === 'dawn' ? 'dawn' : 'long rest');

    // CW custom feat pools (existing path).
    takenFeats.forEach(taken => {
      const def = customFeats.find(f => f.id === taken.id);
      if (!def) return;
      const pool = def.resourcePool;
      if (!pool || !pool.id || !pool.name) return;
      // Prefer the globally registered pool def (richer fields like color),
      // fall back to the embedded pool object.
      const globalDef = getCustomResourcePool(pool.id) || pool;
      const max = globalDef.max ?? globalDef.quantity ?? pool.quantity ?? 1;
      const stored = character.customFeatPools?.[pool.id];
      const current = stored?.current ?? max;
      const recharge = formatRecharge(globalDef.recharge || globalDef.rechargeOn || pool.rechargeOn);
      out.push({
        type: pool.id,
        classIndex: -2, // sentinel: this is a custom-feat pool resource
        name: globalDef.name || pool.name,
        icon: globalDef.icon || '✨', // ✨
        current,
        max,
        dieSize: globalDef.dieSize && globalDef.dieSize !== 'none' ? globalDef.dieSize : null,
        recharge,
        color: globalDef.color || 'purple',
        description: globalDef.description || `Custom resource pool from feat: ${def.name || 'Unnamed'}`,
        subclassKey: null,
      });
    });

    // [FIX MAY 2026 — P1-8]: SRD feat pools (e.g. Martial Adept's Superiority
    //   Die). FeatsPanel.addFeat now bridges activeAbilities[id] with dieSize
    //   into registerCustomResourcePool with a deterministic id pattern:
    //     srd_feat_<featId>_<abilityId>
    //   We surface those here alongside CW pools so a Battle Master / Martial
    //   Adept superiority die appears in ClassResourcesPanel next to Ki / Rage.
    takenFeats.forEach(taken => {
      if (!taken.activeUses) return;
      Object.keys(taken.activeUses).forEach(abilityId => {
        const poolId = `srd_feat_${taken.id}_${abilityId}`;
        const globalDef = getCustomResourcePool(poolId);
        if (!globalDef) return; // not all activeAbilities are pools — only those with dieSize register
        const max = globalDef.max ?? globalDef.quantity ?? 1;
        const stored = character.customFeatPools?.[poolId];
        const current = stored?.current ?? taken.activeUses[abilityId] ?? max;
        out.push({
          type: poolId,
          classIndex: -2,
          name: globalDef.name || abilityId,
          icon: globalDef.icon || '⚔️',
          current,
          max,
          dieSize: globalDef.dieSize && globalDef.dieSize !== 'none' ? globalDef.dieSize : null,
          recharge: formatRecharge(globalDef.recharge || globalDef.rechargeOn),
          color: globalDef.color || 'purple',
          description: globalDef.description || `Resource granted by feat: ${taken.name || taken.id}`,
          subclassKey: null,
        });
      });
    });

    return out;
  };

  // [Phase 4b combat-panel-UI followup — APR 2026]: Concat class + race resources
  // so the panel shows everything the character has (Half-Orc Relentless Endurance,
  // Aasimar Healing Hands, Dragonborn Breath Weapon alongside class resources).
  // [Fix 1.10 — APR 2026]: Append custom-feat pool resources (Sorcery Points,
  // Superiority Dice, etc. authored via the Custom Feat Creator).
  const resources = [...getClassResources(), ...getRaceResources(), ...getCustomFeatPoolResources()];

  // [Spec B Task 5 — 2026-05-16]: Spend Options menus per resource pool.
  //   Query effectCalculator.getSpendActionMenus once; group options by
  //   attachedToResource so each tile can render the merged option list
  //   for its own resource (subclass features that add menus targeting
  //   the same pool merge into the same expanded pane).
  const spendActionMenus = effectCalculator.getSpendActionMenus(character, inventory);
  const optionsByResource = spendActionMenus.reduce((acc, menu) => {
    const key = menu.attachedToResource;
    if (!acc[key]) acc[key] = [];
    acc[key] = [...acc[key], ...menu.options];
    return acc;
  }, {});

  // Per-pool expanded state for the Spend Options pane.
  const [expandedSpendPools, setExpandedSpendPools] = useState(() => new Set());
  const toggleSpendPool = (poolType) => {
    setExpandedSpendPools(prev => {
      const next = new Set(prev);
      if (next.has(poolType)) next.delete(poolType);
      else next.add(poolType);
      return next;
    });
  };
  // Click an option's Spend button → decrement pool by cost + log.
  // [Gen-5-A — 2026-05-30]: costType='none' options are free — no decrement.
  const spendOption = (resource, option) => {
    if (isDemo) return;
    const costType = option.costType || 'integer';
    if (costType !== 'none' && option.cost > resource.current) return; // defensive — button is also disabled
    if (costType !== 'none') {
      updateResource(resource.classIndex, resource.type, -option.cost, resource.name);
    }
    logActivity(
      costType === 'none'
        ? `Used ${option.name} (free)`
        : `Used ${option.cost} ${resource.name}: ${option.name}`,
      'spend_action',
      { resourceType: resource.type, option: option.name, cost: costType === 'none' ? 0 : option.cost },
    );
  };

  // [Sub-plan 1H-G-bw Stone 4 — 2026-05-30]: LoH modal helpers.
  // Defined here (after updateResource + setCharacter) so the closures are correct.
  const openLohModal = (resource) => {
    setLohModalResource(resource);
    setLohModalOpen(true);
  };

  const closeLohModal = () => {
    setLohModalOpen(false);
    setLohModalResource(null);
  };

  /**
   * [Sub-plan 1H-G-bw Stone 4] Handle the resolved LoH spend.
   * resolution.healAmount — HP to restore (0 if cureAction)
   * resolution.cureAction — true → cure disease/poison (costs 5 HP from pool)
   * resolution.target     — free-text ally name ('' = self)
   */
  const handleLohResolve = (resolution) => {
    const resource = lohModalResource;
    if (!resource) return;

    const { healAmount, cureAction, target } = resolution;
    // PHB p.84: curing costs 5 HP from the pool; healing costs healAmount HP.
    const spend = cureAction ? 5 : (healAmount || 0);
    if (spend <= 0) return;

    // 1. Decrement the pool.
    updateResource(resource.classIndex, resource.type, -spend, resource.name);

    // 2. Apply HP to the character sheet (self or solo-play target).
    //    For a named ally the log records it; HP applies to this sheet (solo-play
    //    standard — party-sync will push to their sheet in a future arc).
    if (!cureAction && healAmount > 0) {
      setCharacter(prev => {
        const hp = typeof prev.currentHitPoints === 'number' ? prev.currentHitPoints : 0;
        // maxHP: use the field directly (effectCalculator needs inventory which may
        // not be in scope here with full accuracy; use the stored maxHitPoints instead).
        const maxHp = typeof prev.maxHitPoints === 'number' ? prev.maxHitPoints : hp + healAmount;
        return { ...prev, currentHitPoints: Math.min(hp + healAmount, maxHp) };
      });
    }

    // 3. Activity log (Rule 13).
    if (cureAction) {
      logActivity(
        `Used Lay on Hands to cure disease/poison${target ? ` on ${target}` : ''}`,
        'ability',
        { resource: resource.type, spend: 5, cureAction: true, target: target || 'self' },
      );
    } else {
      logActivity(
        `Spent ${spend} Lay on Hands HP${target ? ` healing ${target}` : ' (self-heal)'}`,
        'ability',
        { resource: resource.type, spend, healAmount, target: target || 'self' },
      );
    }
  };

  // ── Breath Weapon modal helpers ─────────────────────────────────────────────
  // [Sub-plan A2-classres-consumers — 2026-05-30]
  // DC = 8 + CON modifier + proficiency (PHB p.34).
  // Die table: 2d6 (L1-5) | 3d6 (L6-10) | 4d6 (L11-15) | 5d6 (L16+).
  // Save: DEX (line shapes — includes 'Dex.') | CON (cone shapes — includes 'Con.').
  const openBreathWeaponModal = (resource) => {
    const damageType = getBreathWeaponDamageType(character) || 'fire';
    const breathShape = getBreathWeaponShape(character) || '';
    const saveAbility = inferBreathWeaponSave(breathShape);
    const level = character.level || 1;
    const dice = scaleBreathWeaponDice(level);
    const conMod = effectCalculator.getEffectiveModifier(character, inventory, activeQuickBonuses, 'constitution');
    const profBonus = effectCalculator.getProficiencyBonus(character);
    const dc = computeAbilitySaveDc(conMod, profBonus);
    setBreathWeaponModalSpec({
      resource,
      title: `Breath Weapon (${damageType})`,
      rollSpec: {
        rolls: [{ dice, label: `${damageType} damage` }],
        save: { ability: saveAbility, dc, halfOnSave: true },
        targetMode: 'enemy',
      },
    });
    setBreathWeaponModalOpen(true);
  };

  const closeBreathWeaponModal = () => {
    setBreathWeaponModalOpen(false);
    setBreathWeaponModalSpec(null);
  };

  const handleBreathWeaponResolve = (resolution) => {
    const spec = breathWeaponModalSpec;
    if (!spec) return;
    const resource = spec.resource;
    // 1. Decrement raceResource directly (consumeTriggeredSource 'pool' targets
    //    customFeatPools — cannot reach raceResources; use updateResource directly).
    updateResource(resource.classIndex, resource.type, -1, resource.name);
    // 2. Compute actual damage (half on successful save).
    const rolled = resolution.rolledTotals[0] ?? 0;
    const actual = applyHalfOnSave(rolled, resolution.saveOutcome);
    const outcomeNote = resolution.saveOutcome === 'success' ? 'half damage' : 'full damage';
    const targetNote = resolution.target ? ` on ${resolution.target}` : '';
    // 3. Activity log (Rule 13).
    logActivity(
      `Breath Weapon: ${actual} damage (${outcomeNote})${targetNote}`,
      'ability',
      {
        resource: resource.type,
        rolled,
        actual,
        saveOutcome: resolution.saveOutcome,
        target: resolution.target || '',
      },
    );
  };

  // ── Hellish Rebuke modal helpers ─────────────────────────────────────────────
  // [Sub-plan A2-classres-consumers — 2026-05-30]
  // DC = 8 + CHA modifier + proficiency (PHB p.43 — Tiefling spellcasting is CHA).
  // Damage: 3d10 fire (2nd-level cast: 2d10 base + 1d10 upcast from srdSpells.js:579).
  // halfOnSave:true (PHB p.43).
  const openHellishRebukeModal = (resource) => {
    const chaMod = effectCalculator.getEffectiveModifier(character, inventory, activeQuickBonuses, 'charisma');
    const profBonus = effectCalculator.getProficiencyBonus(character);
    const dc = computeAbilitySaveDc(chaMod, profBonus);
    setHellishRebukeModalSpec({
      resource,
      title: 'Hellish Rebuke',
      rollSpec: {
        rolls: [{ dice: '3d10', label: 'fire damage' }],
        save: { ability: 'dexterity', dc, halfOnSave: true },
        targetMode: 'enemy',
      },
    });
    setHellishRebukeModalOpen(true);
  };

  const closeHellishRebukeModal = () => {
    setHellishRebukeModalOpen(false);
    setHellishRebukeModalSpec(null);
  };

  const handleHellishRebukeResolve = (resolution) => {
    const spec = hellishRebukeModalSpec;
    if (!spec) return;
    const resource = spec.resource;
    // 1. Decrement raceResource directly.
    updateResource(resource.classIndex, resource.type, -1, resource.name);
    // 2. Compute actual damage.
    const rolled = resolution.rolledTotals[0] ?? 0;
    const actual = applyHalfOnSave(rolled, resolution.saveOutcome);
    const outcomeNote = resolution.saveOutcome === 'success' ? 'half damage (DEX save)' : 'full damage (save failed)';
    const targetNote = resolution.target ? ` on ${resolution.target}` : '';
    // 3. Activity log (Rule 13).
    logActivity(
      `Hellish Rebuke: ${actual} fire${targetNote} (${outcomeNote})`,
      'ability',
      {
        resource: resource.type,
        rolled,
        actual,
        saveOutcome: resolution.saveOutcome,
        target: resolution.target || '',
      },
    );
  };

  // ── Spend-option rollSpec modal helpers ─────────────────────────────────────
  // [Sub-plan A2-classres-consumers — 2026-05-30]
  // Generic: opens UsableAbilityModal for any spend option with option.rollSpec.
  // Currently wired to Stunning Strike (ki save DC = 8 + prof + WIS, PHB p.78).
  const openSpendOptionModal = (resource, option) => {
    let dc = null;
    if (option.rollSpecDcFormula === 'ki_save_dc') {
      const wisMod = effectCalculator.getEffectiveModifier(character, inventory, activeQuickBonuses, 'wisdom');
      const profBonus = effectCalculator.getProficiencyBonus(character);
      dc = computeAbilitySaveDc(wisMod, profBonus);
    }
    const resolvedSpec = {
      ...option.rollSpec,
      save: { ...option.rollSpec.save, dc },
    };
    setSpendOptionModalState({ resource, option, rollSpec: resolvedSpec });
    setSpendOptionModalOpen(true);
  };

  const closeSpendOptionModal = () => {
    setSpendOptionModalOpen(false);
    setSpendOptionModalState(null);
  };

  const handleSpendOptionResolve = (resolution) => {
    const state = spendOptionModalState;
    if (!state) return;
    const { resource, option } = state;
    // 1. Decrement pool by option cost (same path as spendOption would have used).
    updateResource(resource.classIndex, resource.type, -option.cost, resource.name);
    // 2. Activity log with save outcome.
    const outcomeNote = resolution.saveOutcome === 'failure'
      ? 'target stunned'
      : resolution.saveOutcome === 'success'
        ? 'target saved'
        : '';
    logActivity(
      `Used ${option.cost} ${resource.name}: ${option.name}${outcomeNote ? ` — ${outcomeNote}` : ''}`,
      'spend_action',
      {
        resourceType: resource.type,
        option: option.name,
        cost: option.cost,
        saveOutcome: resolution.saveOutcome,
        target: resolution.target || '',
      },
    );
  };

  if (resources.length === 0) {
    return null; // No resources to display
  }

  const colorClasses = {
    yellow: 'bg-yellow-900/30 border-yellow-700 text-yellow-400',
    blue: 'bg-blue-900/30 border-blue-700 text-blue-400',
    purple: 'bg-purple-900/30 border-purple-700 text-purple-400',
    red: 'bg-red-900/30 border-red-700 text-red-400',
    pink: 'bg-pink-900/30 border-pink-700 text-pink-400',
    cyan: 'bg-cyan-900/30 border-cyan-700 text-cyan-400',
    green: 'bg-green-900/30 border-green-700 text-green-400',
    orange: 'bg-orange-900/30 border-orange-700 text-orange-400',
    gold: 'bg-amber-900/30 border-amber-700 text-amber-400'
  };

  return (
    <div className="bg-gray-800 p-4 rounded-lg">
      <div className="flex justify-between items-center mb-4">
        <h3 className="text-lg font-bold text-white">⚡ Class Resources</h3>
        {/* [REMOVED APR 2026 — F-28]: Rest buttons removed. Use RestPanel for rest actions. */}
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
        {resources.map((resource, idx) => (
          <div
            key={idx}
            className={`border rounded-lg p-3 ${colorClasses[resource.color] || 'bg-gray-700 border-gray-600 text-gray-300'}`}
          >
            <div className="flex justify-between items-start mb-2">
              <div>
                <div className="font-semibold text-sm">
                  {resource.icon} {resource.name}
                </div>
                <div className="text-xs opacity-75 mt-1">
                  {resource.description}
                </div>
                <div className="text-xs opacity-60 mt-1">
                  Recharge: {resource.recharge}
                </div>
              </div>
              {resource.dieSize && (
                <div className="text-xs font-bold bg-black/30 px-2 py-1 rounded">
                  {resource.dieSize}
                </div>
              )}
            </div>

            {/* Resource tracker */}
            <div className="flex items-center justify-between mt-3">
              <button
                onClick={() => updateResource(resource.classIndex, resource.type, -1, resource.name)}
                disabled={isDemo || resource.current <= 0}
                className={`${isMobile ? 'w-10 h-10 min-h-[44px]' : 'w-8 h-8'} bg-black/30 hover:bg-black/50 disabled:opacity-30 rounded flex items-center justify-center text-lg font-bold`}
                aria-label={`Use ${resource.name}`}
              >
                −
              </button>

              <div className="flex items-center gap-2">
                <span className="text-2xl font-bold">
                  {resource.current}
                </span>
                <span className="text-gray-400">/</span>
                <span className="text-xl opacity-75">
                  {resource.max}
                </span>
              </div>

              <button
                onClick={() => updateResource(resource.classIndex, resource.type, +1, resource.name)}
                disabled={isDemo || resource.current >= resource.max}
                className={`${isMobile ? 'w-10 h-10 min-h-[44px]' : 'w-8 h-8'} bg-black/30 hover:bg-black/50 disabled:opacity-30 rounded flex items-center justify-center text-lg font-bold`}
                aria-label={`Restore ${resource.name}`}
              >
                +
              </button>
            </div>

            {/* Visual dots */}
            {resource.max <= 10 && (
              <div className="flex gap-1 mt-2 flex-wrap justify-center">
                {[...Array(resource.max)].map((_, i) => (
                  <div
                    key={i}
                    onClick={() => {
                      if (isDemo) return;
                      const newCurrent = i < resource.current ? i : i + 1;
                      updateResource(resource.classIndex, resource.type, newCurrent - resource.current);
                    }}
                    className={`w-3 h-3 rounded-full cursor-pointer border-2 ${
                      i < resource.current
                        ? 'bg-current border-current'
                        : 'bg-transparent border-current opacity-30'
                    }`}
                  />
                ))}
              </div>
            )}

            {/* [Sub-plan 1H-G-bw Stone 4 — 2026-05-30]: "Spend HP" affordance.
                Renders for any resource with spendHpModal:true (currently
                lay_on_hands). Property-based detection — no string compare.
                Opens UsableAbilityModal in LoH mode (rollSpec=null). */}
            {resource.spendHpModal && (
              <div className="mt-2 border-t border-current/30 pt-2">
                <button
                  type="button"
                  onClick={() => !isDemo && openLohModal(resource)}
                  disabled={isDemo || resource.current <= 0}
                  data-testid={`spend-hp-btn-${resource.type}`}
                  className="w-full py-1.5 text-xs font-semibold bg-amber-700/80 hover:bg-amber-600 disabled:bg-gray-700 disabled:text-gray-500 text-white rounded transition-colors"
                  aria-label={`Spend ${resource.name} HP`}
                >
                  Spend HP
                </button>
              </div>
            )}

            {/* [Sub-plan A2-classres-consumers — 2026-05-30]: "Use" / "Use Reaction"
                affordance for race resources with useAbilityModal:true.
                Currently: Breath Weapon (Dragonborn) + Hellish Rebuke (Tiefling).
                Property-based detection — no resource.type string compare (Rule 11). */}
            {resource.useAbilityModal && (
              <div className="mt-2 border-t border-current/30 pt-2">
                {resource.type === 'breath_weapon' && (
                  <button
                    type="button"
                    onClick={() => !isDemo && openBreathWeaponModal(resource)}
                    disabled={isDemo || resource.current <= 0}
                    data-testid={`use-ability-btn-${resource.type}`}
                    className="w-full py-1.5 text-xs font-semibold bg-orange-700/80 hover:bg-orange-600 disabled:bg-gray-700 disabled:text-gray-500 text-white rounded transition-colors"
                    aria-label={`Use ${resource.name}`}
                  >
                    Use Breath Weapon
                  </button>
                )}
                {resource.type === 'infernal_legacy_hellish_rebuke' && (
                  <button
                    type="button"
                    onClick={() => !isDemo && openHellishRebukeModal(resource)}
                    disabled={isDemo || resource.current <= 0}
                    data-testid={`use-ability-btn-${resource.type}`}
                    className="w-full py-1.5 text-xs font-semibold bg-red-700/80 hover:bg-red-600 disabled:bg-gray-700 disabled:text-gray-500 text-white rounded transition-colors"
                    aria-label={`Use Reaction: ${resource.name}`}
                  >
                    Use Reaction
                  </button>
                )}
              </div>
            )}

            {/* [Spec B Task 5 — 2026-05-16]: Spend Options expandable pane.
                Renders when this resource has any spendActionMenu effects
                attached (e.g., Monk Ki spend: Flurry / Patient / Step /
                Stunning). Each option row shows name + cost badge +
                description tooltip + Spend button. Spend decrements the
                pool by `cost` and logs the action ("Used 1 Ki Points:
                Flurry of Blows"). Disabled when cost > current. */}
            {(optionsByResource[resource.type] || []).length > 0 && (() => {
              const options = optionsByResource[resource.type];
              const isExpanded = expandedSpendPools.has(resource.type);
              return (
                <div className="mt-3 border-t border-current/30 pt-2">
                  <button
                    type="button"
                    onClick={() => toggleSpendPool(resource.type)}
                    aria-expanded={isExpanded}
                    className="w-full flex items-center justify-between text-xs font-semibold opacity-90 hover:opacity-100 transition-opacity"
                  >
                    <span>Spend Options ({options.length})</span>
                    <span aria-hidden="true">{isExpanded ? '▼' : '▶'}</span>
                  </button>
                  {isExpanded && (
                    <div className="mt-2 space-y-1.5">
                      {options.map((opt, optIdx) => {
                        /* [Gen-5-A — 2026-05-30]: costType='none' → free ability.
                           Old options without costType default to 'integer'. */
                        const optCostType = opt.costType || 'integer';
                        const isFree = optCostType === 'none';
                        const cannotAfford = !isFree && opt.cost > resource.current;
                        return (
                          <div
                            key={`${resource.type}:${opt.name}:${optIdx}`}
                            className="flex items-start justify-between gap-2 p-2 bg-black/20 rounded"
                          >
                            <div className="flex-1 min-w-0">
                              <div className="text-xs font-semibold">
                                {opt.name}
                                {isFree ? (
                                  <span className="ml-2 text-[10px] px-1.5 py-0.5 bg-emerald-900/50 text-emerald-300 rounded">
                                    Free
                                  </span>
                                ) : (
                                  <span className="ml-2 text-[10px] px-1.5 py-0.5 bg-black/40 rounded">
                                    Cost {opt.cost}
                                  </span>
                                )}
                              </div>
                              {opt.description && (
                                <div className="text-[11px] opacity-75 mt-0.5" title={opt.description}>
                                  {opt.description}
                                </div>
                              )}
                            </div>
                            <button
                              type="button"
                              onClick={() => {
                                // [Sub-plan A2-classres-consumers — 2026-05-30]:
                                // If option has a rollSpec, open UsableAbilityModal
                                // FIRST (e.g. Stunning Strike CON save). The modal
                                // onResolve handler does the Ki decrement + log.
                                // Property-based — no opt.name string compare (Rule 11).
                                if (opt.rollSpec) {
                                  if (!isDemo && !cannotAfford) openSpendOptionModal(resource, opt);
                                } else {
                                  spendOption(resource, opt);
                                }
                              }}
                              disabled={isDemo || cannotAfford}
                              className="px-2 py-1 text-xs font-semibold bg-current/80 hover:bg-current text-black rounded disabled:opacity-30 disabled:cursor-not-allowed"
                              aria-label={isFree ? `Use ${opt.name} (free)` : `Spend ${opt.cost} ${resource.name} on ${opt.name}`}
                            >
                              {isFree ? 'Use' : 'Spend'}
                            </button>
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>
              );
            })()}
          </div>
        ))}
      </div>

      {/* [A49 AT-2 — 2026-06-03]: Invoke-anytime slot-recovery actions.
          Renders a "Use <actionName>" button for every slotRecoveryAction
          whose invokeAnytime===true and whose pool has current > 0.
          Property-driven — no class/action name strings (Rule 11/16/17).
          RestPanel keeps rendering ALL slot-recovery actions (rest is a
          superset — Arcane Recovery is fine to invoke mid-rest too). */}
      {(() => {
        const allActions = effectCalculator.getSlotRecoveryActions(character, inventory);
        const anytimeActions = allActions.filter(a => a.invokeAnytime === true);
        if (anytimeActions.length === 0) return null;
        return (
          <div className="mt-4 border-t border-gray-700 pt-4">
            <div className="text-xs text-gray-500 uppercase tracking-wide mb-2">Class Actions</div>
            <div className="space-y-2">
              {anytimeActions.map((action, idx) => (
                <button
                  key={`${action.classKey}:${action.poolResourceId}:${idx}`}
                  type="button"
                  data-testid={`invoke-slot-recovery-${action.poolResourceId}`}
                  onClick={() => !isDemo && setActiveSlotRecoveryAction(action)}
                  disabled={isDemo}
                  className="w-full p-2 bg-purple-700 hover:bg-purple-600 disabled:opacity-50 text-white rounded-lg text-left text-sm font-semibold transition-colors"
                >
                  Use {action.actionName}
                </button>
              ))}
            </div>
          </div>
        );
      })()}

      {/* [A49 AT-2 — 2026-06-03]: SlotRecoveryModal for invoke-anytime actions.
          Same modal RestPanel uses — no fork. onConfirm mirrors RestPanel's
          implementation (setSpellSlots decrement + setCharacter pool decrement
          + logActivity). */}
      <SlotRecoveryModal
        isOpen={!!activeSlotRecoveryAction}
        actionName={activeSlotRecoveryAction?.actionName}
        resolvedBudget={activeSlotRecoveryAction?.resolvedBudget || 0}
        maxSlotTier={activeSlotRecoveryAction?.maxSlotTier || 5}
        currentSlots={spellSlots}
        classLevel={activeSlotRecoveryAction?.classLevel || 1}
        poolNounPlural={activeSlotRecoveryAction?.poolNounPlural}
        tierNoun={activeSlotRecoveryAction?.tierNoun}
        onCancel={() => setActiveSlotRecoveryAction(null)}
        onConfirm={(slotDeltas) => {
          if (!activeSlotRecoveryAction) return;
          const action = activeSlotRecoveryAction;
          // Apply slot deltas via shared pure helper (Rule 22 — extract→import→assert).
          setSpellSlots(prev => applySlotDeltas(prev, slotDeltas));
          // Decrement the action's pool via shared pure helper.
          setCharacter(prev => decrementActionPool(prev, action));
          // Log activity (Rule 13).
          const summaryParts = Object.entries(slotDeltas || {})
            .filter(([, n]) => n > 0)
            .map(([lvl, n]) => `+${n} L${lvl}`);
          logActivity(
            `Used ${action.actionName}: ${summaryParts.join(', ') || 'no slots restored'}`,
            'class_resource',
            { feature: action.actionName, slotDeltas },
          );
          setActiveSlotRecoveryAction(null);
        }}
      />

      {/* [Sub-plan 1H-G-bw Stone 4 — 2026-05-30]: LoH "Spend HP" resolution modal.
          rollSpec=null → UsableAbilityModal renders HP-input + cure checkbox only.
          costSpend is echo-only (not consumed by the modal itself; handleLohResolve
          calls updateResource directly for the pool decrement). */}
      <UsableAbilityModal
        isOpen={lohModalOpen}
        onClose={closeLohModal}
        title={lohModalResource?.name || 'Lay on Hands'}
        rollSpec={null}
        costSpend={lohModalResource ? { type: 'pool', poolId: lohModalResource.type, classKey: null } : null}
        rollProtocol="d20"
        maxPoolHp={lohModalResource?.current ?? null}
        onResolve={handleLohResolve}
      />

      {/* [Sub-plan A2-classres-consumers — 2026-05-30]: Breath Weapon resolution modal.
          rollSpec carries the ancestry-resolved damage type + save + DC.
          costSpend is echo-only; handleBreathWeaponResolve decrements raceResources directly. */}
      {breathWeaponModalSpec && (
        <UsableAbilityModal
          isOpen={breathWeaponModalOpen}
          onClose={closeBreathWeaponModal}
          title={breathWeaponModalSpec.title}
          rollSpec={breathWeaponModalSpec.rollSpec}
          costSpend={{ type: 'raceResource', id: breathWeaponModalSpec.resource?.type }}
          rollProtocol="d20"
          maxPoolHp={null}
          onResolve={handleBreathWeaponResolve}
        />
      )}

      {/* [Sub-plan A2-classres-consumers — 2026-05-30]: Hellish Rebuke resolution modal.
          Proactive "Use Reaction" button — on-take-damage interrupt pipeline is deferred.
          3d10 fire, DEX save, halfOnSave:true. costSpend echo-only; handler decrements directly. */}
      {hellishRebukeModalSpec && (
        <UsableAbilityModal
          isOpen={hellishRebukeModalOpen}
          onClose={closeHellishRebukeModal}
          title={hellishRebukeModalSpec.title}
          rollSpec={hellishRebukeModalSpec.rollSpec}
          costSpend={{ type: 'raceResource', id: hellishRebukeModalSpec.resource?.type }}
          rollProtocol="d20"
          maxPoolHp={null}
          onResolve={handleHellishRebukeResolve}
        />
      )}

      {/* [Sub-plan A2-classres-consumers — 2026-05-30]: Spend-option rollSpec modal.
          Generic: any spend option with option.rollSpec opens this modal BEFORE
          decrementing. Currently used for Stunning Strike (CON save). */}
      {spendOptionModalState && (
        <UsableAbilityModal
          isOpen={spendOptionModalOpen}
          onClose={closeSpendOptionModal}
          title={spendOptionModalState.option?.name || 'Spend'}
          rollSpec={spendOptionModalState.rollSpec}
          costSpend={{ type: 'pool', poolId: spendOptionModalState.resource?.type, amount: spendOptionModalState.option?.cost }}
          rollProtocol="d20"
          maxPoolHp={null}
          onResolve={handleSpendOptionResolve}
        />
      )}
    </div>
  );
};

// [PERFORMANCE]: Wrap in React.memo to prevent unnecessary re-renders
export default React.memo(ClassResourcesPanel);
